import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CDataTable,
  CInput,
  CInputGroup,
  CInputGroupAppend,
} from '@coreui/react'
import HexColorLabel from './HexColorLabel'
import { ModelApi, SavedListViewApi } from 'src/api'
import Paginator from 'src/components/Paginator'
import { ActionButton } from 'src/views/buttons'
import TableFieldsSelector from './TableFieldsSelector'
import PermissionsMiddleware from 'src/reusable/PermissionsMiddleware'
import ReportApi from 'src/api/reportApi'
import { emptySafe, statusColumn } from './DjangoListColumnParsers'
import { DjangoFormModal } from './DjangoForm'

import './DjangoList.scss'
import { useIsRootUser, useToken } from 'src/state/ducks/auth/hooks'
import { useLoader } from 'src/state/ducks/loader/hooks'
import { useModal } from 'src/state/ducks/modal/hooks'
import { useToast } from 'src/state/ducks/toast/hooks'
import useDocumentationModal from './useDocumentationModal'
import LegacyDjangoList from './LegacyDjangoList'
import { EmptyTableSlot, FormButton, isMobile } from './DjangoListShared'
import FilterBar from './listViews/FilterBar'
import SavedViewsTabs, { ViewNameDialog } from './listViews/SavedViewsTabs'
import {
  appliedFilters,
  defaultListState,
  filtersToParams,
  readRememberedState,
  rememberState,
  stateForView,
  statesEqual,
  viewState,
} from './listViews/listState'

export {
  EmptyTableSlot,
  FormButton,
  isMobile,
  parseReportFilterForm,
} from './DjangoListShared'

// Columns chosen before the list state existed (TableFieldsSelector used
// to keep them on its own)
const legacyColumns = (djangoApp, djangoModel) => {
  try {
    const columns = JSON.parse(
      localStorage.getItem(`tableFields/${djangoApp}/${djangoModel}`)
    )
    return Array.isArray(columns) && columns.length ? columns : null
  } catch (e) {
    return null
  }
}

const ListViewsDjangoList = ({
  djangoApp,
  djangoModel,
  formsAsModal,
  fields: propsFields = [],
  scopedSlots: propsScopedSlots = {},
  header: propsHeader = {},
  onReload,
  onDeletion,
  defaultSort,
  aditionalActions: AditionalActions = null,
  cerateButtonLabel = 'Novo',
  defaultFilters = {},
  // Always sent, even when the user applies filters or a text search
  fixedFilters = {},
  documentationPath = null,
}) => {
  const loadingAction = `prevenX/list/${djangoModel}`
  const formRoute = `/${djangoApp}/${djangoModel}/form`

  const token = useToken()
  const [startLoading, stopLoading] = useLoader()
  const showModal = useModal()
  const showToast = useToast()
  const showDocs = useDocumentationModal()

  const [data, setData] = useState(null)
  const [title, setTitle] = useState(null)
  const [header, setHeader] = useState(propsHeader)
  const [createPath, setCreatePath] = useState(null)
  const [updatePath, setUpdatePath] = useState(null)
  const [deletePath, setDeletePath] = useState(null)
  const [page, setPage] = useState(1)
  const [paginator, setPaginator] = useState(null)
  const [filterDefinitions, setFilterDefinitions] = useState([])
  const [djangoReportFilterCallback, setDjangoReportFilterCallback] =
    useState(null)
  const [djangoReportFilterForm, setDjangoReportFilterForm] = useState(null)

  // Filters, text search, ordering and columns of the list ("list state"),
  // remembered per list between visits, plus the saved view it came from
  // (null is the built-in "Padrão" view, built from the list props).
  const defaultFiltersKey = JSON.stringify(defaultFilters || {})
  const defaultState = useMemo(
    () => defaultListState(defaultFilters, defaultSort),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultFiltersKey, defaultSort]
  )
  const [{ activeViewId, state: listState }, setListView] = useState(
    () =>
      readRememberedState(djangoApp, djangoModel) || {
        activeViewId: null,
        state: {
          ...defaultState,
          columns: legacyColumns(djangoApp, djangoModel),
        },
      }
  )
  const [textInput, setTextInput] = useState(listState.textFilter)
  const [views, setViews] = useState([])
  const [viewsLoaded, setViewsLoaded] = useState(false)
  const [viewNameDialog, setViewNameDialog] = useState(null)

  const order = listState.order || undefined
  const textFilter = listState.textFilter || null

  const updateListState = (changes, resetPage = true) => {
    setListView((current) => ({
      ...current,
      state: { ...current.state, ...changes },
    }))
    if (resetPage) setPage(1)
  }

  const filterParams = () => {
    const definitions = filterDefinitions.reduce(
      (acc, definition) => ({ ...acc, [definition.name]: definition }),
      {}
    )
    // Filters remember their type, so the first request (made before the
    // list sends its filter definitions) already builds the right params
    const filters = appliedFilters(listState.filters, listState.textFilter)
    filters.forEach(({ field, type }) => {
      if (type && !definitions[field]) definitions[field] = { type }
    })
    return filtersToParams(filters, definitions)
  }

  useEffect(() => {
    rememberState(djangoApp, djangoModel, activeViewId, listState)
  }, [djangoApp, djangoModel, activeViewId, listState])

  useEffect(() => {
    let cancelled = false
    setViewsLoaded(false)
    SavedListViewApi.shared
      .list(token, djangoApp, djangoModel)
      .then(
        (data) => !cancelled && setViews(data || []),
        () => !cancelled && setViews([])
      )
      .finally(() => !cancelled && setViewsLoaded(true))
    return () => {
      cancelled = true
    }
  }, [token, djangoApp, djangoModel])

  const activeView = views.find((view) => view.id === activeViewId) || null
  // Ignores the text search, which isn't part of a view
  const referenceState = stateForView(activeView, defaultState, listState)
  const isDirty = viewsLoaded && !statesEqual(listState, referenceState)

  const applyView = (view) => {
    const state = stateForView(view, defaultState, listState)
    setListView({ activeViewId: view ? view.id : null, state })
    setPage(1)
  }

  const handleViewError = (message) => (e) => {
    console.error(e)
    showToast(e?.response?.data?.message || message, { appearance: 'error' })
  }

  const createView = (name) =>
    SavedListViewApi.shared
      .create(token, djangoApp, djangoModel, name, viewState(listState))
      .then((created) => {
        setViews((current) => [...current, created])
        setListView((current) => ({ ...current, activeViewId: created.id }))
        setViewNameDialog(null)
        showToast('Visão salva.', { appearance: 'success' })
      }, handleViewError('Erro ao salvar a visão.'))

  const updateView = (view, changes, successMessage) =>
    SavedListViewApi.shared.update(token, view.id, changes).then((updated) => {
      setViews((current) =>
        current.map((item) => (item.id === updated.id ? updated : item))
      )
      setViewNameDialog(null)
      showToast(successMessage, { appearance: 'success' })
    }, handleViewError('Erro ao salvar a visão.'))

  const deleteView = (view) =>
    showModal({
      title: `Excluir a visão "${view.name}"?`,
      body: 'Os registros não são afetados, só a visão salva.',
      onConfirm: () =>
        SavedListViewApi.shared.delete(token, view.id).then(() => {
          setViews((current) => current.filter((item) => item.id !== view.id))
          if (view.id === activeViewId)
            setListView((current) => ({ ...current, activeViewId: null }))
          showToast('Visão excluída.', { appearance: 'success' })
        }, handleViewError('Erro ao excluir a visão.')),
    })

  const handleReportFilter = useCallback(
    async (djangoApp, reportPath) => {
      const { data } = await ReportApi.shared.getReportForm(
        token,
        djangoApp,
        reportPath
      )

      if (data?.form?.ordered_fields?.length > 0)
        setDjangoReportFilterForm(data.form)
      else return { data: {} }

      return await new Promise((resolve, reject) =>
        setDjangoReportFilterCallback([resolve, reject])
      )
    },
    [token, setDjangoReportFilterForm]
  )

  let fields = [],
    scopedSlots = {
      status: statusColumn,
      urgency_level_color: ({ urgency_level_color }) => (
        <td>
          <HexColorLabel hexCode={urgency_level_color} />
        </td>
      ),
      actions: ({ id }) => (
        <td align="right" style={{ whiteSpace: 'nowrap' }}>
          {!!createPath && (
            <PermissionsMiddleware
              djangoApp={djangoApp}
              djangoModel={djangoModel}
              permissionType="can_change"
            >
              <FormButton
                formsAsModal={true}
                copyId={id}
                title="Copiar"
                iconName="cil-copy"
                djangoApp={djangoApp}
                djangoModel={djangoModel}
                onReload={() => (onReload ? onReload(loadData) : loadData())}
              />
            </PermissionsMiddleware>
          )}
          {!!updatePath && (
            <PermissionsMiddleware
              djangoApp={djangoApp}
              djangoModel={djangoModel}
              permissionType="can_change"
            >
              <FormButton
                formsAsModal={formsAsModal}
                objectId={id}
                title="Editar"
                iconName="cil-pencil"
                djangoApp={djangoApp}
                djangoModel={djangoModel}
                onReload={() => onReload && onReload(loadData)}
                formRoute={formRoute}
              />
            </PermissionsMiddleware>
          )}
          {!!deletePath && (
            <PermissionsMiddleware
              djangoApp={djangoApp}
              djangoModel={djangoModel}
              permissionType="can_delete"
            >
              <ActionButton
                label="Excluir"
                onClick={() => {
                  showModal({
                    onConfirm: () => handleDeletion(id),
                    title: 'Deseja mesmo excluir o registro?',
                    body: 'Esta ação não poderá ser desfeita',
                    doubleClickToConfirm: true,
                  })
                }}
                iconName="cil-trash"
              />
            </PermissionsMiddleware>
          )}
        </td>
      ),
    }
  if (propsFields.length !== undefined) {
    fields = propsFields
    scopedSlots = {
      ...scopedSlots,
      ...propsScopedSlots,
    }
  } else {
    fields = Object.keys(propsFields)
    fields.forEach((key) => {
      if (propsFields[key]) scopedSlots[key] = propsFields[key]
      else if (!scopedSlots[key])
        scopedSlots[key] = (row) => emptySafe(row[key])
    })
  }
  const customFields = listState.columns
  if (customFields)
    customFields.forEach((key) => {
      if (!propsFields[key] && !scopedSlots[key])
        scopedSlots[key] = (row) => emptySafe(row[key])
    })

  const filteredFields = [
    ...(customFields || fields).filter((field) => field !== 'actions'),
    {
      key: 'actions',
      _style: { width: '10rem', textAlign: 'right' },
    },
  ]

  const loadData = (inputOrder, dumpCache = false) => {
    if (dumpCache) {
      ModelApi.shared._clearMemos()
    }
    ModelApi.shared
      .list(
        token,
        djangoApp,
        djangoModel,
        {
          ...fixedFilters,
          ...filterParams(),
        },
        page,
        10,
        inputOrder || order,
        textFilter
      )
      .then(
        ({ data = {} }) => {
          setData(data.data)
          setTitle(data.title)
          setHeader({
            ...data.header,
            ...header,
          })
          setCreatePath(data.create_path)
          setUpdatePath(data.update_path)
          setDeletePath(data.delete_path)
          setFilterDefinitions(data.filters || [])
          setPaginator(data.paginator)
          setPage((data.paginator || {}).current_page_number)
        },
        (e) => {
          console.error(e)
          setData([])
          showToast('Erro ao carregar os registros.', { appearance: 'error' })
        }
      )
  }

  const handleDeletion = (id) => {
    if (onDeletion) onDeletion(id)
    else {
      startLoading(loadingAction)

      ModelApi.shared
        .delete(token, djangoApp, djangoModel, id)
        .then(
          () => {
            loadData()
            showToast('Sucesso ao deletar registro.', {
              appearance: 'success',
            })
          },
          (e) => {
            console.error(e)
            showToast('Erro ao deletar registro.', { appearance: 'error' })
          }
        )
        .finally(() => stopLoading(loadingAction))
    }
  }

  const search = () => {
    if ((textInput || '') === (listState.textFilter || '')) loadData()
    else updateListState({ textFilter: textInput || '' })
  }

  const capitalizedHeader = { ...header }
  Object.entries(capitalizedHeader).forEach(([key, value]) => {
    capitalizedHeader[key] = (value || '').toTitleCase()
  })

  const queryKey = JSON.stringify([
    djangoApp,
    djangoModel,
    page,
    listState.filters,
    listState.textFilter,
    listState.order,
  ])
  useEffect(() => {
    if (djangoApp && djangoModel) loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey])

  return (
    <CCard
      className="django-list"
      style={{
        margin: '0 -15px', // Compensate default layout padding
      }}
    >
      <CCardHeader>
        {title && title.toTitleCase()}
        <div className="card-header-actions">
          {AditionalActions && (
            <AditionalActions
              listFilters={
                appliedFilters(listState.filters, listState.textFilter).length
                  ? filterParams()
                  : undefined
              }
              loadData={loadData}
            />
          )}
          {documentationPath && (
            <ActionButton
              label="Ajuda"
              iconName="cil-file"
              fullWidth
              color="primary"
              variant="outlined"
              className="mt-3"
              onClick={() => showDocs(documentationPath)}
            />
          )}
          {!!createPath && (
            <PermissionsMiddleware
              djangoApp={djangoApp}
              djangoModel={djangoModel}
              permissionType="can_add"
            >
              {isMobile.any() ? (
                <ActionButton
                  label="Exportar"
                  iconName="cil-file"
                  fullWidth
                  color="primary"
                  variant="outlined"
                  className="mt-3"
                  onClick={() =>
                    handleReportFilter(djangoApp, djangoModel)
                      .then(({ data }) =>
                        ReportApi.shared._fileDownload(
                          token,
                          ReportApi.shared.getReportUrl(
                            djangoApp,
                            djangoModel,
                            {
                              ...data,
                              company: ReportApi.shared.companyHeader,
                            },
                            'pdf'
                          ),
                          'teste.pdf'
                        )
                      )
                      .then(
                        () =>
                          showToast('Arquivo baixado', {
                            appearance: 'success',
                          }),
                        () =>
                          showToast('Erro ao baixar o arquivo', {
                            appearance: 'error',
                          })
                      )
                  }
                >
                  Imprimir PDF
                </ActionButton>
              ) : (
                <ActionButton
                  label="Exportar"
                  iconName="cil-file"
                  onClick={() =>
                    handleReportFilter(djangoApp, djangoModel).then(
                      ({ data }) =>
                        ReportApi.shared.openReportUrl(
                          token,
                          djangoApp,
                          djangoModel,
                          {
                            ...data,
                            company: ReportApi.shared.companyHeader,
                          }
                        ),
                      () => null
                    )
                  }
                />
              )}

              <FormButton
                formsAsModal={formsAsModal}
                title={cerateButtonLabel}
                iconName="cil-pencil"
                djangoApp={djangoApp}
                djangoModel={djangoModel}
                onReload={() => onReload && onReload(loadData)}
                formRoute={formRoute}
                style={{ marginLeft: '.7rem' }}
              />
            </PermissionsMiddleware>
          )}
        </div>
      </CCardHeader>
      <CCardBody>
        {/* The text search sits above the views: it isn't part of a view
            and keeps applying to whichever tab is selected */}
        <div className="list-search-bar">
          <CInputGroup className="list-search">
            <CInput
              placeholder="Digite um termo para pesquisar"
              value={textInput || ''}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyDown={(event) => event.key === 'Enter' && search()}
            />
            <CInputGroupAppend>
              <CButton color="primary" onClick={search}>
                Pesquisar
              </CButton>
            </CInputGroupAppend>
          </CInputGroup>
        </div>
        <SavedViewsTabs
          views={views}
          activeViewId={activeView ? activeView.id : null}
          isDirty={isDirty}
          onSelect={applyView}
          onSaveNew={() => setViewNameDialog({ mode: 'create' })}
          onSave={() =>
            updateView(
              activeView,
              { state: viewState(listState) },
              'Visão atualizada.'
            )
          }
          onDiscard={() => applyView(activeView)}
          onRename={(view) => setViewNameDialog({ mode: 'rename', view })}
          onDelete={deleteView}
        />
        <div className="list-filters-bar">
          {/* Pills only make sense once the list described its filters */}
          {data !== null && (
            <FilterBar
              definitions={filterDefinitions}
              filters={listState.filters}
              searching={!!listState.textFilter.trim()}
              onChange={(filters) => updateListState({ filters })}
            />
          )}
          {!isMobile.any() && (
            <div className="list-columns">
              <TableFieldsSelector
                value={listState.columns}
                onRowChange={(rows) =>
                  updateListState(
                    { columns: rows.equals(fields) ? null : rows },
                    false
                  )
                }
                defaultFields={fields}
                fields={header}
                djangoModel={djangoModel}
                djangoApp={djangoApp}
              />
            </div>
          )}
        </div>
        <CDataTable
          hover
          striped
          bordered
          size="sm"
          items={data}
          responsive={false}
          header={!isMobile.any()}
          fields={filteredFields}
          columnHeaderSlot={{
            ...capitalizedHeader,
            actions: 'Ações',
            last_login: 'Último login',
          }}
          noItemsViewSlot={<EmptyTableSlot />}
          sorter={{
            external: true,
          }}
          sorterValue={
            order && {
              column: (order || '').replace('-', ''),
              asc: order && order[0] !== '-',
            }
          }
          onSorterValueChange={({ column, asc }) => {
            if (column) {
              const identifier = (asc ? '' : '-') + column
              if (identifier !== order)
                updateListState({ order: identifier }, false)
            }
          }}
          scopedSlots={{
            ...scopedSlots,
          }}
        />
        {!!(paginator || {}).current_page_number && (
          <Paginator
            pageNumber={(paginator || {}).current_page_number || 1}
            lastPage={(paginator || {}).last_page_number || 1}
            onChange={setPage}
          />
        )}
      </CCardBody>
      <ViewNameDialog
        open={!!viewNameDialog}
        title={
          viewNameDialog?.mode === 'rename' ? 'Renomear visão' : 'Salvar visão'
        }
        initialName={viewNameDialog?.view?.name || ''}
        onClose={() => setViewNameDialog(null)}
        onSubmit={(name) =>
          viewNameDialog?.mode === 'rename'
            ? updateView(viewNameDialog.view, { name }, 'Visão renomeada.')
            : createView(name)
        }
      />
      {djangoReportFilterForm && (
        <DjangoFormModal
          showRelatedFieldsActions={false}
          form={djangoReportFilterForm}
          submit={false}
          // TODO: Remove text filter
          onClose={() => {
            setDjangoReportFilterForm(null)
            djangoReportFilterCallback && djangoReportFilterCallback[1]()
          }}
          onSubmit={(data) => {
            Object.entries(data).forEach(([key, value]) => {
              if (value === undefined || value === null) delete data[key]
            })
            djangoReportFilterCallback &&
              djangoReportFilterCallback[0]({
                data: {
                  ...data,
                  company: ReportApi.shared.companyHeader,
                },
                form: { ...djangoReportFilterForm },
              })
            setDjangoReportFilterForm(null)
          }}
        />
      )}
    </CCard>
  )
}

// Filter pills and saved views are only shown to the `root` user for now
// (same gate as the custom dashboards); everyone else keeps the previous
// list. Remove LegacyDjangoList and this switch to release them.
const DjangoList = (props) =>
  useIsRootUser() ? (
    <ListViewsDjangoList {...props} />
  ) : (
    <LegacyDjangoList {...props} />
  )

export default DjangoList
